import React, { useEffect, useState } from 'react';
import { 
    Dialog, 
    DialogTitle, 
    DialogContent, 
    IconButton, 
    CircularProgress, 
    Box,
    Typography,
    Divider
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';

const PreviewJSON = ({ open, fileItem, onClose }) => {
    const [loading, setLoading] = useState(true);
    const [jsonData, setJsonData] = useState(null);
    const [renderError, setRenderError] = useState(null);

    useEffect(() => {
        const loadJsonDoc = async () => {
            if (!open || !fileItem) return;

            setLoading(true);
            setRenderError(null);
            
            try {
                let textData;
                if (fileItem.blob) {
                    textData = await fileItem.blob.text();
                } else if (fileItem.file instanceof File) {
                    textData = await fileItem.file.text();
                } else if (fileItem.url) {
                    const response = await fetch(fileItem.url);
                    if (!response.ok) throw new Error("Network response was not ok");
                    textData = await response.text();
                } else {
                    throw new Error("No valid file data found to render.");
                }
                
                try {
                    const parsed = JSON.parse(textData);
                    setJsonData(JSON.stringify(parsed, null, 2));
                } catch (parseError) {
                    setJsonData(textData); 
                }
            } catch (err) {
                console.error("Error reading JSON file:", err);
                setRenderError(err.message || "Failed to read file");
            } finally {
                setLoading(false);
            }
        };

        loadJsonDoc();
    }, [fileItem, open]);

    return (
        <Dialog 
            open={open} 
            onClose={onClose} 
            maxWidth="lg" 
            fullWidth
            PaperProps={{ sx: { borderRadius: 3 } }}
        >
            <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', bgcolor: 'background.paper', py: 2 }}>
                <Typography variant="h6" sx={{ fontWeight: 600 }} noWrap>
                    {fileItem?.name || "Náhľad JSON dát"}
                </Typography>
                <IconButton onClick={onClose} size="small" sx={{ bgcolor: 'action.hover' }}>
                    <CloseIcon fontSize="small" />
                </IconButton>
            </DialogTitle>
            
            <Divider />

            <DialogContent dividers sx={{ bgcolor: 'background.default', p: { xs: 2, md: 3 } }}>
                {loading && (
                    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '60vh' }}>
                        <CircularProgress />
                    </Box>
                )}

                {renderError && (
                    <Box sx={{ p: 4, textAlign: 'center', height: '60vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <Typography color="error" variant="body1" sx={{ fontWeight: 500 }}>
                            Chyba pri načítaní dokumentu: {renderError}
                        </Typography>
                    </Box>
                )}
                
                {!loading && !renderError && jsonData && (
                    <Box 
                        component="pre"
                        sx={(theme) => ({ 
                            height: '70vh', 
                            overflow: 'auto',
                            m: 0,
                            p: 3,
                            bgcolor: theme.palette.mode === 'dark' ? theme.palette.background.default : '#1e1e1e',
                            color: theme.palette.mode === 'dark' ? '#e2e8f0' : '#d4d4d4',
                            borderRadius: 2,
                            fontFamily: 'monospace',
                            fontSize: '0.875rem',
                            whiteSpace: 'pre-wrap',
                            wordWrap: 'break-word',
                        })} 
                    >
                        {jsonData}
                    </Box>
                )}
            </DialogContent>
        </Dialog>
    );
};

export default PreviewJSON;